import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { SITTER } from "@/constants/testIds";
import { toast } from "sonner";
import { Loader2, Save, Sparkles } from "lucide-react";

const ALL_CERTS = [
  "RBT", "BCBA", "BCaBA", "Special Education Teacher",
  "Occupational Therapy Assistant", "Speech Therapy",
  "Registered Nurse", "CNA", "CPR", "First Aid",
  "Crisis Prevention (CPI)", "PCM", "Safety Care"
];

const LANGS = ["English", "Spanish", "French", "Mandarin", "Cantonese", "Arabic", "Portuguese", "Vietnamese", "Tagalog", "ASL", "Russian", "Hindi"];
const GENDERS = ["", "female", "male", "non-binary", "prefer not to say"];
const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const RESPITE_SERVICES = [
  ["traditional_respite", "Traditional respite"],
  ["overnight_support", "Overnight support"],
  ["travel_assistance", "Travel assistance"],
  ["party_buffers", "Party buffers"],
  ["community_support", "Community support"],
];

function Field({ label, testId, textarea, ...props }) {
  const Cmp = textarea ? "textarea" : "input";
  return (
    <div>
      <label className="field-label">{label}</label>
      <Cmp {...props} data-testid={testId}
        rows={textarea ? 3 : undefined}
        className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500 outline-none" />
    </div>
  );
}

function ChipToggle({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${
        active ? "bg-sage-600 text-white border-sage-600" : "bg-white border-stone2-200 text-sage-800 hover:border-sage-400"
      }`}>
      {children}
    </button>
  );
}

export default function SitterProfileEdit() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const adminEditSitterId = params.get("admin_edit") || "";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState(null);

  useEffect(() => {
    // When admin is editing on behalf, load the target sitter via the admin endpoint.
    const loader = adminEditSitterId
      ? api.get(`/admin/sitters/${adminEditSitterId}`).then(({ data }) => data)
      : api.get("/sitters/me").then(({ data }) => data);
    loader.then((data) => setF(data))
      .catch((e) => toast.error(extractError(e))).finally(() => setLoading(false));
  }, [adminEditSitterId]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleArr = (k, v) => setF((s) => {
    const arr = s[k] || [];
    return { ...s, [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] };
  });

  const updateDaySlots = (day, value) => {
    // value is a comma-separated string like "09:00-12:00, 14:00-18:00"
    const slots = value.split(",").map((s) => s.trim()).filter(Boolean);
    setF((s) => ({ ...s, availability: { ...(s.availability || {}), [day]: slots } }));
  };

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...f };
      payload.hourly_rate = parseFloat(payload.hourly_rate) || 25;
      payload.experience_years = parseInt(payload.experience_years) || 0;
      payload.service_radius_miles = parseInt(payload.service_radius_miles) || 15;
      // strip fields the API doesn't accept in input model
      delete payload.id; delete payload.user_id; delete payload.verified;
      delete payload.verifications; delete payload.rating_avg; delete payload.rating_count;
      delete payload.name; delete payload.email; delete payload.created_at; delete payload.updated_at;
      // Admin editing on behalf posts to the admin endpoint.
      if (adminEditSitterId) {
        await api.put(`/admin/sitters/${adminEditSitterId}/profile`, payload);
        toast.success("Sitter profile saved (admin)");
        navigate("/admin");
        return;
      }
      await api.put("/sitters/me", payload);
      toast.success("Profile saved");
      navigate("/sitter");
    } catch (err) { toast.error(extractError(err)); }
    finally { setSaving(false); }
  };

  if (loading || !f) return (<div className="min-h-screen bg-stone2-50"><Navbar /><div className="p-10 text-stone2-500">Loading…</div></div>);

  return (
    <div className="min-h-screen bg-stone2-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <span className="section-eyebrow">Sitter profile</span>
        {adminEditSitterId && (
          <div className="mt-3 rounded-xl border-2 border-amber-400 bg-amber-50 p-4 flex items-start gap-3" data-testid="admin-impersonation-banner">
            <Sparkles className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-heading font-bold text-amber-900">
                Admin mode — editing on behalf of this sitter
              </div>
              <p className="mt-1 text-sm text-amber-800 leading-relaxed">
                Anything you save here writes to the sitter's own record. Verification flags and Stripe Connect stay locked — use the admin verification panel to approve documents.
              </p>
              <button type="button" onClick={() => navigate(`/admin`)}
                className="mt-2 text-xs font-semibold text-amber-900 underline hover:text-amber-700"
                data-testid="admin-back-to-sitter-detail">
                ← Back to admin console
              </button>
            </div>
          </div>
        )}
        <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900">Your professional profile</h1>

        <form onSubmit={submit} data-testid={SITTER.profileForm} className="mt-8 space-y-6">
          <section className="paper-card p-6 grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <label className="field-label flex items-center justify-between">
                <span>Elevator pitch <span className="text-ochre-600">★</span></span>
                <span className="text-xs text-stone2-500">{(f.elevator_pitch || "").length}/280</span>
              </label>
              <textarea
                data-testid="sitter-elevator-pitch"
                maxLength={280}
                rows={2}
                value={f.elevator_pitch || ""}
                onChange={(e) => set("elevator_pitch", e.target.value)}
                placeholder="One strong sentence — this shows to families when you first reach out. e.g., 'RBT with 5 years of ASD experience — bilingual EN/ES, calm through meltdowns.'"
                className="w-full rounded-lg border border-stone2-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500"
              />
              <p className="text-xs text-stone2-500 mt-1">
                Shown to families in your first message preview. The stronger this line, the higher your accept rate.
              </p>
            </div>
            <Field textarea label="Biography" value={f.biography} onChange={(e) => set("biography", e.target.value)} testId="sitter-bio" className="sm:col-span-2" />
            <Field label="Years of experience" type="number" min="0" value={f.experience_years} onChange={(e) => set("experience_years", e.target.value)} testId="sitter-exp-years" />
            <Field label="Hourly rate ($)" type="number" min="1" step="0.5" value={f.hourly_rate} onChange={(e) => set("hourly_rate", e.target.value)} testId="sitter-rate" />
            <Field textarea label="Experience description" value={f.experience_description} onChange={(e) => set("experience_description", e.target.value)} testId="sitter-exp-desc" />
            <Field textarea label="Education" value={f.education} onChange={(e) => set("education", e.target.value)} testId="sitter-education" />

            <Field label="City" value={f.city} onChange={(e) => set("city", e.target.value)} testId="sitter-city" />
            <Field label="State" value={f.state} onChange={(e) => set("state", e.target.value)} testId="sitter-state" />
            <Field label="ZIP" value={f.zip_code} onChange={(e) => set("zip_code", e.target.value)} testId="sitter-zip" />
            <Field label="Service radius (miles)" type="number" min="1" value={f.service_radius_miles} onChange={(e) => set("service_radius_miles", e.target.value)} testId="sitter-radius" />

            <div>
              <label className="field-label">Gender</label>
              <select value={f.gender || ""} onChange={(e) => set("gender", e.target.value)} data-testid="sitter-gender"
                className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm">
                {GENDERS.map((g) => <option key={g} value={g}>{g || "prefer not to say"}</option>)}
              </select>
            </div>
          </section>

          <section className="paper-card p-6">
            <h2 className="font-heading font-semibold text-sage-900">Certifications</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {ALL_CERTS.map((c) => (
                <ChipToggle key={c} active={(f.certifications || []).includes(c)}
                  onClick={() => toggleArr("certifications", c)}
                  testId={`sitter-cert-toggle-${c}`}>{c}</ChipToggle>
              ))}
            </div>
          </section>

          <section className="paper-card p-6">
            <h2 className="font-heading font-semibold text-sage-900">Languages</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {LANGS.map((l) => (
                <ChipToggle key={l} active={(f.languages || []).includes(l)}
                  onClick={() => toggleArr("languages", l)}
                  testId={`sitter-lang-${l}`}>{l}</ChipToggle>
              ))}
            </div>
          </section>

          <section className="paper-card p-6">
            <h2 className="font-heading font-semibold text-sage-900">Comfort & capabilities</h2>
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              {[
                ["aba_trained", "ABA trained"],
                ["special_ed_experience", "Special education experience"],
                ["non_smoker", "Non-smoker"],
                ["can_transport", "Can transport child"],
                ["comfortable_aggressive_behaviors", "Comfortable with aggressive behaviors"],
                ["comfortable_toileting", "Comfortable with toileting"],
                ["overnight_care", "Overnight care"],
                ["travel_support", "Travel support"],
              ].map(([k, l]) => (
                <label key={k} className="flex items-center gap-3 p-3 rounded-lg border border-stone2-200 bg-white hover:border-sage-300 cursor-pointer transition-colors duration-200">
                  <input type="checkbox" checked={!!f[k]} onChange={(e) => set(k, e.target.checked)}
                    data-testid={`sitter-flag-${k}`}
                    className="rounded text-sage-600 focus:ring-sage-500" />
                  <span className="text-sm text-sage-900">{l}</span>
                </label>
              ))}
            </div>
          </section>

          <section className="paper-card p-6">
            <h2 className="font-heading font-semibold text-sage-900">Respite services offered</h2>
            <p className="text-xs text-stone2-500 mt-1">Families search by these categories. Only select what you're trained and willing to provide.</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {RESPITE_SERVICES.map(([k, l]) => {
                const active = (f.respite_services || []).includes(k);
                return (
                  <label key={k}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors duration-200 ${
                      active ? "border-sage-600 bg-sage-50" : "border-stone2-200 bg-white hover:border-sage-300"
                    }`}
                    data-testid={`sitter-respite-${k}`}>
                    <input type="checkbox" checked={active} onChange={() => toggleArr("respite_services", k)}
                      className="rounded text-sage-600 focus:ring-sage-500" />
                    <span className="text-sm text-sage-900">{l}</span>
                  </label>
                );
              })}
            </div>
          </section>

          <section className="paper-card p-6">
            <h2 className="font-heading font-semibold text-sage-900">Availability</h2>
            <p className="text-xs text-stone2-500 mt-1">Enter time slots per day, comma-separated. Example: <code>09:00-12:00, 14:00-18:00</code></p>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              {DAYS.map((d) => (
                <div key={d}>
                  <label className="field-label capitalize">{d}</label>
                  <input value={(f.availability?.[d] || []).join(", ")}
                    onChange={(e) => updateDaySlots(d, e.target.value)}
                    data-testid={`sitter-avail-${d}`}
                    placeholder="09:00-12:00, 14:00-18:00"
                    className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm" />
                </div>
              ))}
            </div>
          </section>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => navigate("/sitter")} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} data-testid={SITTER.profileSave} className="btn-primary">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save profile
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
